import { Card, EmptyState, Loading, PageHead } from "@corecrm/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useOpenQuestions } from "../api/queries";
import type { Question } from "../api/types";
import { LoadError } from "../components/bits";
import { QuestionDialog, QuestionItem } from "../components/Questions";

/** Open questions grouped by project, in the order the projects first appear. */
export function byProject(questions: Question[]): { project: Question["project"]; items: Question[] }[] {
  const groups = new Map<string, { project: Question["project"]; items: Question[] }>();
  for (const q of questions) {
    const group = groups.get(q.project.id) ?? { project: q.project, items: [] };
    group.items.push(q);
    groups.set(q.project.id, group);
  }
  return [...groups.values()];
}

/** Everything the team is waiting on you to answer, by project. */
export function QuestionsPage() {
  const questions = useOpenQuestions();
  const [opened, setOpened] = useState<Question | null>(null);
  if (questions.isPending) return <Loading />;
  if (questions.error)
    return (
      <LoadError
        error={questions.error}
        onRetry={() => questions.refetch()}
        what="questions"
        back={["/", "Home"]}
      />
    );
  return (
    <>
      <PageHead title="Open questions" sub="What your project team needs from you" />
      {questions.data.length === 0 ? (
        <EmptyState>Nothing to answer right now.</EmptyState>
      ) : (
        byProject(questions.data).map((g) => (
          <Card
            key={g.project.id}
            title={
              <h2>
                <Link to={`/projects/${g.project.id}`}>{g.project.name}</Link>
              </h2>
            }
          >
            <div className="item-list">
              {g.items.map((q) => (
                <QuestionItem key={q.id} question={q} onOpen={setOpened} />
              ))}
            </div>
          </Card>
        ))
      )}
      {opened && <QuestionDialog question={opened} onClose={() => setOpened(null)} />}
    </>
  );
}
